import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActiveConnection, ProjectsAndIssueTypes } from '../types';
import { getProjectsAndIssueTypes } from '../utils/clients/walnutApi';

type UseIntegrationProjectsFromTemplates = {
  activeConnections: ActiveConnection[];
  enterpriseId: string;
  apiHostname: string;
  authFn: () => Promise<string>;
  setConnectionIdsWithLoadingErrors: Dispatch<SetStateAction<string[]>>;
};

export type ProjectsByConnections = Record<string, ProjectsAndIssueTypes[]>;

const useIntegrationProjectsFromTemplates = ({
  activeConnections,
  enterpriseId,
  apiHostname,
  authFn,
  setConnectionIdsWithLoadingErrors
}: UseIntegrationProjectsFromTemplates) => {
  const { t } = useTranslation();

  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [projectsByConnections, setProjectsByConnections] =
    useState<ProjectsByConnections>({});
  const [projectsError, setProjectsError] = useState<Error | null>(null);

  useEffect(() => {
    if (!activeConnections.length) {
      return;
    }

    const fetchProjects = async () => {
      setIsProjectsLoading(true);
      setConnectionIdsWithLoadingErrors([]);

      try {
        const token = await authFn();

        const projectsConnectionsMap: Record<string, ProjectsAndIssueTypes[]> =
          {};
        const connectionsWithLoadingErrors: string[] = [];

        for (const { id, templates } of activeConnections) {
          const usedProjectsFromTemplatesSet = new Set(
            templates?.map(temp => temp.project_id)
          );

          if (!templates?.length || !usedProjectsFromTemplatesSet.size) {
            continue;
          }

          try {
            const connectionProjects = await getProjectsAndIssueTypes({
              apiHostname,
              token,
              enterpriseId,
              connectionId: id
            });

            const templatesProjects = connectionProjects?.filter(pr =>
              usedProjectsFromTemplatesSet.has(pr.id)
            );

            projectsConnectionsMap[id] = templatesProjects;
          } catch (connectionError) {
            connectionsWithLoadingErrors.push(id);
            // continue loading other connections projects even if some connections failed to load projects
            continue;
          }
        }

        setProjectsByConnections(projectsConnectionsMap);

        if (connectionsWithLoadingErrors.length) {
          setConnectionIdsWithLoadingErrors(connectionsWithLoadingErrors);
          throw new Error(t('Some connections failed to load projects'));
        }
      } catch (error) {
        setProjectsError(error as Error);
      } finally {
        setIsProjectsLoading(false);
      }
    };

    fetchProjects();
  }, [activeConnections, enterpriseId]);

  return {
    projectsByConnections,
    isProjectsLoading,
    projectsError
  };
};

export default useIntegrationProjectsFromTemplates;
